// frontend/src/pages/ScoringInfoPage.tsx
import React, { useEffect, ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { mixpanelTrack } from '@/services/mixpanel';
import { Target, Zap, KeyRound, Handshake, MoveRight, Footprints, XCircle, Icon } from 'lucide-react';
import { soccerBall } from '@lucide/lab';
import SEO from '@/components/common/SEO';
import { ScoringSection, DailyHowToSection, useNextContestCta } from '@/components/scoring/HowItWorksCarousel';
import HowToPlayLeaguesCarousel from '@/components/scoring/HowToPlayLeaguesCarousel';
import TopPlayersSection from '@/components/scoring/TopPlayersSection';
import FilmRoomSection from '@/components/scoring/FilmRoomSection';

// --- Data Types ---

interface Example {
    question: string;
    videoUrl: string;
    matchDesc: string;
    points: string[];
}

interface ExpandableContent {
    desc: string;
    rules?: string[];
    examples: Example[];
}

// --- Scoring Data ---

const GCA_CONTENT: ExpandableContent = {
    desc: "Goal/Shot creations are the 2 offensive actions immediately leading to a goal/shot (assists, hockey assists, fouls won, shots leading to another shot, etc.). Think of GCs like Passing TDs.",
    examples: [
        {
            question: "Video Explainer - How can a Shot be a GC?",
            videoUrl: "https://x.com/ChelseaFC/status/2029358022145855706",
            matchDesc: "Chelsea v. Aston Villa. Cole Palmer passes to Reece James, Reece James shoots, shot is saved, rebound is struck by Cole Palmer for a Goal.",
            points: [
                "Cole Palmer gets the Goal (8), Shot (1), and SOT (3)",
                "Reece James gets a GC (6) for the shot that was saved",
                "Cole Palmer gets a GC (6) for the pass to Reece James",
            ],
        },
        {
            question: "Video Explainer - How can a Foul Won be a GC?",
            videoUrl: "https://x.com/FondreMediah/status/2028188339124175324",
            matchDesc: "Man United v. Crystal Palace. Bruno Fernandes passes to Cunha who is fouled in the penalty area. Bruno scores on the penalty.",
            points: [
                "Bruno Fernandes gets the Goal (8), Shot (1), and SOT (3)",
                "Matheus Cunha gets a GC (6) for the foul won",
                "Bruno Fernandes gets a GC (6) for the pass to Cunha",
                "Bruno Fernandes gets an IP (1) for the pass to Cunha",
            ],
        },
        {
            question: "Video Explainer - How can a pass be a GC?",
            videoUrl: "https://x.com/Citeh1894s/status/2027846838100103179",
            matchDesc: "Man City v. Leeds. Cherki passes to Alt-Nouri who passes to Semenyo for the goal.",
            points: [
                "Semenyo gets the Goal (8), Shot (1), Shot on Target (3)",
                "Alt-Nouri gets a GC (6) for the assist",
                "Cherki gets a GC (6) and IP (1) for the pass to Alt-Nouri",
            ],
        },
    ],
};

const PROG_CONTENT: ExpandableContent = {
    desc: "Impact Passes/Carries are passes or carries ending in the attacking half that move the ball 10 yards closer to the goal than it has been recently. Think of these as Passing/Rushing Yards.",
    examples: [
        {
            question: "Video Explainer - How can a pass/carry be an IP/IC?",
            videoUrl: "https://x.com/JDokuXtra/status/2028864586481594827",
            matchDesc: "Man City v. Nottingham Forest. Haaland passes to Doku. Doku carries into the box and scores.",
            points: [
                "Doku gets the Goal (8), Shot (1), Shot on Target (3), and IC (1) for the Carry",
                "Haaland gets the GC (6) and IP (1) for the pass to Doku",
            ],
        },
    ],
};

// --- Icons ---

const ExternalLinkIcon: React.FC = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" className="inline ml-1">
        <path d="M11 7.5V11.5C11 12.0523 10.5523 12.5 10 12.5H2.5C1.94772 12.5 1.5 12.0523 1.5 11.5V4C1.5 3.44772 1.94772 3 2.5 3H6.5M8.5 1.5H12.5M12.5 1.5V5.5M12.5 1.5L5.5 8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// --- Stat Icons ---

const STAT_ICONS: Record<string, ReactNode> = {
    'G': <Icon iconNode={soccerBall} size={16} className="text-accent-yellow" />,
    'SOT': <Target size={16} className="text-accent-yellow" />,
    'Sh': <Zap size={16} className="text-accent-yellow" />,
    'GC': <KeyRound size={16} className="text-accent-yellow" />,
    'SC': <Handshake size={16} className="text-accent-yellow" />,
    'IP': <MoveRight size={16} className="text-accent-yellow" />,
    'IC': <Footprints size={16} className="text-accent-yellow" />,
    'DIS': <XCircle size={16} className="text-secondary" />,
};

// --- Reusable sub-components ---

const StatRow: React.FC<{ name: string; abbr: string; points: number; negative?: boolean }> = ({ name, abbr, points, negative }) => (
    <div className="flex items-center justify-between px-5 py-3">
        <div className="flex items-center gap-2 min-w-0">
            {STAT_ICONS[abbr]}
            <span className="text-white font-semibold">{name}</span>
        </div>
        <div className="text-right">
            <span className={`text-xl font-bold ${negative ? 'text-secondary' : 'text-accent-yellow'}`}>
                {points > 0 ? '+' : ''}{points}
            </span>
            <span className="text-xs text-gray-400 ml-1">pts</span>
        </div>
    </div>
);

const ExampleCard: React.FC<{ example: Example }> = ({ example }) => (
    <div className="bg-primary rounded-lg p-4 space-y-2">
        <p className="text-accent-yellow font-bold text-sm">{example.question}</p>
        {example.videoUrl && (
            <a
                href={example.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-accent-light-blue underline hover:text-accent-light-blue/80 transition-colors"
            >
                Watch the play
                <ExternalLinkIcon />
            </a>
        )}
        <p className="text-sm text-gray-400">{example.matchDesc}</p>
        <div className="space-y-1 pt-1">
            {example.points.map((p, pIdx) => (
                <p key={pIdx} className="text-sm text-gray-300">{p}</p>
            ))}
        </div>
    </div>
);

const DrillDownContent: React.FC<{ content: ExpandableContent }> = ({ content }) => (
    <div className="px-5 py-4 space-y-4 border-t border-gray-700/50">
        <p className="text-sm text-gray-300 leading-relaxed">{content.desc}</p>
        {content.rules && (
            <ul className="text-sm text-gray-400 space-y-1.5 pl-4">
                {content.rules.map((rule, rIdx) => (
                    <li key={rIdx} className="list-disc">{rule}</li>
                ))}
            </ul>
        )}
        {content.examples.map((ex, eIdx) => (
            <ExampleCard key={eIdx} example={ex} />
        ))}
    </div>
);

// --- Comparison Data ---

interface ComparisonRow {
    left: string;
    right: string;
}

interface PlayerComparisonRow {
    leftName: string;
    leftPos: string;
    leftPpg: string;
    rightName: string;
    rightPos: string;
    rightPpg: string;
}

const SCORING_COMPARISONS: ComparisonRow[] = [
    { left: 'Rushing / Rec TDs', right: 'Goals' },
    { left: 'Passing TDs', right: 'Goal Creations' },
    { left: 'Passing Yards', right: 'Impact Passes' },
    { left: 'Rushing / Rec Yards', right: 'Impact Carries' },
    { left: 'PPR', right: 'Shot' },
    { left: 'Field Goal', right: 'Shot on Target' },
    { left: 'Turnovers', right: 'Dispossessed' },
];

const POSITION_COMPARISONS: ComparisonRow[] = [
    { left: 'Top QBs', right: 'Top Attacking MFs' },
    { left: 'Fantasy Starter QBs', right: 'Box-to-Box MFs' },
    { left: 'Goal-line RBs', right: 'Strikers' },
    { left: 'WRs', right: 'Forwards' },
    { left: 'Pass Catching RBs', right: 'Wingers' },
    { left: 'TEs', right: 'Defenders' },
    { left: 'Kickers', right: 'Keepers' },
];

const PLAYER_COMPARISONS: PlayerComparisonRow[] = [
    { leftName: 'Josh Allen', leftPos: 'Elite QB', leftPpg: '25 PPG', rightName: 'Bruno Fernandes', rightPos: 'Elite Attacking MF', rightPpg: '25 PPG' },
    { leftName: 'Derrick Henry', leftPos: 'Elite RB', leftPpg: '20 PPG', rightName: 'Erling Haaland', rightPos: 'Elite Striker', rightPpg: '20 PPG' },
    { leftName: 'Puka Nacua', leftPos: 'Elite WR', leftPpg: '20 PPG', rightName: 'Mohamed Salah', rightPos: 'Elite FW', rightPpg: '20 PPG' },
    { leftName: 'Jahmyr Gibbs', leftPos: 'Elite Pass Catching RB', leftPpg: '18 PPG', rightName: 'Bukayo Saka', rightPos: 'Elite Winger', rightPpg: '18 PPG' },
    { leftName: 'Trey McBride', leftPos: 'Elite TE', leftPpg: '15 PPG', rightName: 'Pedro Porro', rightPos: 'Elite Attacking DF', rightPpg: '15 PPG' },
    { leftName: 'Brandon Aubrey', leftPos: 'Kicker', leftPpg: '8 PPG', rightName: 'Jordan Pickford', rightPos: 'Goalkeeper', rightPpg: '8 PPG' },
];

// --- Comparison Sub-components ---

const ArrowIcon: React.FC = () => (
    <svg width="24" height="12" viewBox="0 0 24 12" fill="none" className="text-accent-yellow flex-shrink-0">
        <polyline points="2,6 18,6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <polyline points="14,2 20,6 14,10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ComparisonChart: React.FC<{ title: string; rows: ComparisonRow[] }> = ({ title, rows }) => (
    <div className="bg-surface-card rounded-lg overflow-hidden border border-gray-700">
        <div className="bg-surface-raised px-4 py-3">
            <h3 className="text-xs font-heading font-bold uppercase tracking-widest text-accent-yellow text-center">{title}</h3>
        </div>
        {/* Column headers */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b-2 border-accent-yellow/30">
            <span className="text-caption font-heading font-bold uppercase tracking-wider text-gray-400 flex-1 text-right pr-3">Fantasy Football</span>
            <span className="w-6" />
            <span className="text-caption font-heading font-bold uppercase tracking-wider text-gray-400 flex-1 text-left pl-3">Fantasy Futbol</span>
        </div>
        <div className="divide-y divide-gray-700/30">
            {rows.map((row, idx) => (
                <div key={idx} className="flex items-center px-4 h-[52px]">
                    <span className="flex-1 text-right pr-3 text-sm text-gray-300">{row.left}</span>
                    <ArrowIcon />
                    <span className="flex-1 text-left pl-3 text-sm text-white font-semibold">{row.right}</span>
                </div>
            ))}
        </div>
    </div>
);

const PlayerComparisonChart: React.FC<{ rows: PlayerComparisonRow[] }> = ({ rows }) => (
    <div className="bg-surface-card rounded-lg overflow-hidden border border-gray-700">
        <div className="bg-surface-raised px-4 py-3">
            <h3 className="text-xs font-heading font-bold uppercase tracking-widest text-accent-yellow text-center">How Players Translate</h3>
        </div>
        {/* Column headers */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b-2 border-accent-yellow/30">
            <span className="text-caption font-heading font-bold uppercase tracking-wider text-gray-400 flex-1 text-right pr-3">Fantasy Football</span>
            <span className="w-6" />
            <span className="text-caption font-heading font-bold uppercase tracking-wider text-gray-400 flex-1 text-left pl-3">Fantasy Futbol</span>
        </div>
        <div className="divide-y divide-gray-700/30">
            {rows.map((row, idx) => (
                <div key={idx} className="flex items-center px-4 h-[52px]">
                    <div className="flex-1 text-right pr-3">
                        <p className="text-sm text-white font-semibold leading-tight">{row.leftName}</p>
                        <p className="text-caption text-white/60 leading-tight">{row.leftPos} &middot; {row.leftPpg}</p>
                    </div>
                    <ArrowIcon />
                    <div className="flex-1 text-left pl-3">
                        <p className="text-sm text-white font-semibold leading-tight">{row.rightName}</p>
                        <p className="text-caption text-accent-yellow/70 leading-tight">{row.rightPos} &middot; {row.rightPpg}</p>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

// --- Component ---

const ScoringInfoPage: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    // ONE next-contest fetch for the whole page (shared with both carousel
    // sections via onCtaClick - review catch 2026-07-22: each section
    // fetching its own copy doubled the request).
    const { nextContestId, handleCtaClick } = useNextContestCta();

    // Leagues vs Daily explainer tabs. LEAGUES is primary and the default
    // (owner 2026-07-22: recruiting commissioners, league is the pitch);
    // ?tab=daily deep-links the DFS explainer.
    const [searchParams, setSearchParams] = useSearchParams();
    const howToPlayTab: 'leagues' | 'daily' = searchParams.get('tab') === 'daily' ? 'daily' : 'leagues';
    const handleTabSelect = (tab: 'leagues' | 'daily') => {
        // Functional update: only touch the `tab` key. Bar-QR links carry
        // `?ref=` attribution that must survive tab switches (re-shared URLs
        // keep crediting the bar).
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.delete('tab');
            if (tab === 'daily') next.set('tab', 'daily');
            return next;
        }, { replace: true });
        mixpanelTrack('How To Play Tab Selected', {
            'User ID': user?.id,
            'Tab': tab,
        });
    };

    useEffect(() => {
        mixpanelTrack('Viewed Scoring Rules', {
            'User ID': user?.id,
        });
    }, [user?.id]);

    return (
        <div className="min-h-screen bg-primary text-gray-200 font-body">
            <SEO
                title="How to Play & Scoring System | Fantasy Futbol"
                description="NFL-style fantasy soccer for the Premier League and La Liga. Same Scoring. Same Strategy. Different Football."
                path="/scoring"
            />
            {/* Card-swipe section. Scoring ALWAYS leads, above the tab toggle
                (owner 2026-07-22): it's shared knowledge - leagues and daily
                score identically. The pills only swap the lifecycle explainer. */}
            <div className="py-8 md:py-10 px-4">
                <div className="max-w-5xl mx-auto space-y-8 md:space-y-12">
                    <ScoringSection onCtaClick={handleCtaClick} />
                    <div className="flex justify-center gap-2 !mt-8 md:!mt-10">
                        <button
                            onClick={() => handleTabSelect('leagues')}
                            className={`px-5 py-2 rounded-lg text-sm font-heading font-bold uppercase tracking-wider transition-colors ${
                                howToPlayTab === 'leagues'
                                    ? 'bg-accent-yellow text-primary'
                                    : 'bg-surface-card text-accent-light-blue border border-gray-700/60 hover:border-accent-yellow/50'
                            }`}
                        >
                            Leagues
                        </button>
                        <button
                            onClick={() => handleTabSelect('daily')}
                            className={`px-5 py-2 rounded-lg text-sm font-heading font-bold uppercase tracking-wider transition-colors ${
                                howToPlayTab === 'daily'
                                    ? 'bg-accent-yellow text-primary'
                                    : 'bg-surface-card text-accent-light-blue border border-gray-700/60 hover:border-accent-yellow/50'
                            }`}
                        >
                            Daily
                        </button>
                    </div>
                    {howToPlayTab === 'leagues' ? <HowToPlayLeaguesCarousel /> : <DailyHowToSection onCtaClick={handleCtaClick} />}
                    <FilmRoomSection />
                </div>
            </div>

            {/* Top 10 EPL by FPPG — proof of concept + dual CTA out to Build / Research */}
            <TopPlayersSection />

            {/*
              ========================================================================
              HIDDEN: Original deep-dive sections preserved for future re-use.
              The card-swipe carousel above replaces these for now. To restore,
              flip the `false &&` guard below to `true &&` (or remove it entirely).
              ========================================================================
            */}
            {false && (
                <>
                    {/* Comparison Charts */}
                    <div className="py-12 px-4">
                        <div className="max-w-7xl mx-auto">
                            <h2 className="text-2xl md:text-3xl font-heading font-bold text-center text-white mb-8">The Game You Love for the Game You Love</h2>

                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5">
                                <ComparisonChart title="How Scoring Translates" rows={SCORING_COMPARISONS} />
                                <ComparisonChart title="How Player Types Translate" rows={POSITION_COMPARISONS} />
                                <PlayerComparisonChart rows={PLAYER_COMPARISONS} />
                            </div>
                        </div>
                    </div>

                    {/* Scoring Content */}
                    <div className="py-12 px-4">
                        <div className="max-w-5xl mx-auto">
                            <h2 className="text-3xl font-heading font-bold text-center text-white mb-3">The Scoring System</h2>
                            <p className="text-center text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
                                Just like fantasy football, players only score points with the ball in their hands (or at their feet). The players who dominate the ball dominate fantasy.
                            </p>

                            {/* Zone 1: Simple scoring types — two mini category cards side by side */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                                {/* Attacking */}
                                <div className="bg-surface-card rounded-lg overflow-hidden border border-gray-700">
                                    <div className="bg-surface-raised px-5 py-3">
                                        <h3 className="text-sm font-bold uppercase tracking-widest text-accent-light-blue">ATTACKING</h3>
                                    </div>
                                    <div className="divide-y divide-gray-700/50">
                                        <StatRow name="Goal" abbr="G" points={8} />
                                        <StatRow name="Shot on Target" abbr="SOT" points={3} />
                                        <StatRow name="Shot" abbr="Sh" points={1} />
                                    </div>
                                </div>

                                {/* Turnovers */}
                                <div className="bg-surface-card rounded-lg overflow-hidden border border-gray-700">
                                    <div className="bg-surface-raised px-5 py-3">
                                        <h3 className="text-sm font-bold uppercase tracking-widest text-secondary">TURNOVERS</h3>
                                    </div>
                                    <StatRow name="Dispossessed" abbr="DIS" points={-1} negative />
                                </div>
                            </div>

                            {/* Zone 2: Deep dive — always-expanded detail sections */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                {/* Left: Creating (GCA / SCA) */}
                                <div className="bg-surface-card rounded-lg overflow-hidden border border-gray-700">
                                    <div className="bg-surface-raised px-5 py-3">
                                        <h3 className="text-sm font-bold uppercase tracking-widest text-accent-light-blue">CREATING</h3>
                                    </div>
                                    <div className="divide-y divide-gray-700/50">
                                        <StatRow name="Goal Creation" abbr="GC" points={6} />
                                        <StatRow name="Shot Creation" abbr="SC" points={1.5} />
                                    </div>
                                    <DrillDownContent content={GCA_CONTENT} />
                                </div>

                                {/* Right: Progressing (IP / IC) */}
                                <div className="bg-surface-card rounded-lg overflow-hidden border border-gray-700">
                                    <div className="bg-surface-raised px-5 py-3">
                                        <h3 className="text-sm font-bold uppercase tracking-widest text-accent-light-blue">PROGRESSING</h3>
                                    </div>
                                    <div className="divide-y divide-gray-700/50">
                                        <StatRow name="Impact Pass" abbr="IP" points={1} />
                                        <StatRow name="Impact Carry" abbr="IC" points={1} />
                                    </div>
                                    <DrillDownContent content={PROG_CONTENT} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* CTA */}
                    <div className="py-12 px-4">
                        <div className="max-w-md mx-auto text-center">
                            <h2 className="text-2xl font-heading font-bold text-white mb-4">Ready to play?</h2>
                            <button
                                onClick={() => navigate(nextContestId ? `/dfs/build/${nextContestId}` : '/contests')}
                                className="w-full py-4 px-8 bg-accent-yellow text-primary font-heading font-bold text-lg uppercase tracking-wider rounded-lg hover:bg-accent-yellow/90 transition-colors"
                            >
                                Build Your Lineup
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ScoringInfoPage;
